<template>
  <main class="card-transfer-page">
    <AppNavBar :title="pageTitle" />

    <section class="card-preview" @click="openCardPicker">
      <app-image class="card-preview__bg" :src="cardBlueBg" alt="" />
      <div class="card-preview__content">
        <div class="card-preview__block">
          <span>{{ cardTitleText }}</span>
          <div data-strong>{{ maskedCardNo }}</div>
        </div>
        <div class="card-preview__block card-preview__block--right">
          <span>{{ t("card.cardBalance") }}</span>
          <div data-strong>{{ cardBalanceText }}</div>
        </div>
      </div>
    </section>

    <section class="panel-card">
      <template v-if="isTransferIn">
        <header class="section-title">
          <h2>{{ t("card.rechargeCurrencyTitle") }}</h2>
        </header>

        <div class="asset-row">
          <app-image :src="usdtIcon" alt="" />
          <div class="asset-row__content">
            <div data-strong>{{ balanceSymbol }}</div>
            <span>{{
              `${t("card.availableBalanceLabel")}${availableBalanceText}`
            }}</span>
          </div>
        </div>

        <div class="hint-line">
          <span>{{ t("card.exchangeRate") }}</span>
          <div data-strong>{{ priceText }}</div>
        </div>

        <div class="dash-line dash-line--spacious" />
      </template>

      <header
        class="section-title"
        :class="{ 'section-title--amount': isTransferIn }"
      >
        <div class="section-title__row">
          <h2>{{ amountTitle }}</h2>
          <span>{{ `（${t("card.minAmount")} ${minAmountText}）` }}</span>
        </div>
      </header>

      <van-field
        v-model="amount"
        type="number"
        clearable
        input-align="left"
        class="amount-field"
        :placeholder="amountPlaceholder"
      >
        <template #button>
          <div class="amount-field__actions">
            <span class="amount-symbol">{{ inputSymbol }}</span>
            <van-button
              size="small"
              type="primary"
              class="amount-all-btn"
              @click="fillAll"
            >
              {{ t("wallet.all") }}
            </van-button>
          </div>
        </template>
      </van-field>

      <div v-if="!isTransferIn" class="hint-line hint-line--compact">
        <span>{{ t("card.exchangeRate") }}</span>
        <div data-strong>{{ priceText }}</div>
      </div>

      <div class="dash-line" />

      <div class="result-row">
        <div>{{ arrivalLabel }}</div>
        <div data-strong>{{ arrivalText }}</div>
      </div>

      <div class="dash-line" />

      <div class="result-row">
        <div>{{ feeLabel }}</div>
        <div data-strong>{{ feeText }}</div>
      </div>
    </section>

    <van-button
      block
      round
      type="primary"
      class="submit-btn"
      :loading="submitting"
      @click="submitTransfer"
    >
      {{ t("card.submitTransfer") }}
    </van-button>

    <van-popup
      v-model:show="showCardPicker"
      position="bottom"
      round
      class="card-picker-popup"
    >
      <section class="card-picker">
        <header class="card-picker__header">
          <div data-strong>{{ t("card.mycard") }}</div>
          <button type="button" @click="showCardPicker = false">
            <van-icon name="cross" />
          </button>
        </header>

        <div class="card-picker__list">
          <button
            v-for="item in cards"
            :key="item.id"
            type="button"
            class="card-picker__item"
            :class="{ active: String(item.id) === currentCardId }"
            @click="selectCard(item)"
          >
            <div>
              <div data-strong>{{ formatCardCode(item) }}</div>
              <span>{{ formatCardBalance(item) }}</span>
            </div>
            <van-icon
              :name="String(item.id) === currentCardId ? 'success' : 'circle'"
            />
          </button>
        </div>
      </section>
    </van-popup>

    <PayPasswordPopup
      v-model:show="showPayPassword"
      v-model:password="payPassword"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :forgot-text="t('auth.forgotPasswordRecovery')"
      show-forgot
      :confirm-disabled="!payPassword.trim()"
      @confirm="confirmPassword"
      @cancel="resetPayPasswordPopup"
      @closed="resetPayPasswordPopup"
      @forgot="goForgotPassword"
    />

    <van-popup v-model:show="showSuccessPopup" round class="success-popup">
      <section class="success-dialog">
        <button
          type="button"
          class="success-dialog__close"
          @click="handleSuccessDone"
        >
          <van-icon name="cross" />
        </button>
        <app-image :src="successIcon" alt="" class="success-dialog__icon" />
        <div data-strong>{{ t("card.transferSuccessTitle") }}</div>
        <p>{{ successDescription }}</p>
        <van-button block round type="primary" @click="handleSuccessDone">
          {{ t("card.reviewDone") }}
        </van-button>
      </section>
    </van-popup>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { closeToast, showFailToast, showLoadingToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import PayPasswordPopup from "@/components/PayPasswordPopup.vue";
import { payPwdCheck } from "@/api/auth";
import {
  pcCardCharge,
  pcCardChargeInfo,
  pcCardWithdraw,
  pcCardWithdrawInfo,
} from "@/api/card";
import { useCardStore } from "@/stores/card";
import { useProfileStore } from "@/stores/profile";
import { getDeviceId } from "@/utils/device";
import { getRouteState, navigateWithState } from "@/utils/route-state";
import { normalizeFeeRate } from "@/view/card/utils";
import cardBlueBg from "@/assets/images/cardImage/card_blue_bg.png";
import successIcon from "@/assets/images/cardImage/blue_yes_icon.png";
import usdtIcon from "@/assets/images/usdt.png";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const cardStore = useCardStore();
const profileStore = useProfileStore();
const pageState = computed(() => getRouteState(route, "card-transfer", {}));

const info = ref({});
const amount = ref("");
const showCardPicker = ref(false);
const showPayPassword = ref(false);
const payPassword = ref("");
const showSuccessPopup = ref(false);
const submitting = ref(false);
const currentCardId = ref(String(pageState.value.cardId || ""));

const isTransferIn = computed(() => pageState.value.mode !== "out");
const cardType = computed(() => Number(pageState.value.cardType || 2));
const cards = computed(() => cardStore.cardsByType(cardType.value));
const currentCard = computed(() => {
  const matched = cards.value.find(
    (item) => String(item?.id || "") === currentCardId.value,
  );
  if (matched) {
    return matched;
  }
  return {
    id: pageState.value.cardId || "",
    network: pageState.value.network || "",
    bin: pageState.value.bin || "",
    last4: pageState.value.last4 || "",
    channelType: pageState.value.channelType || "",
    status: pageState.value.status || "",
    balance: {
      available: pageState.value.balanceAvailable || "0.00",
      currency: pageState.value.balanceCurrency || "USD",
    },
  };
});

const pageTitle = computed(() =>
  isTransferIn.value ? t("card.transferIn") : t("card.transferOut"),
);
const balanceSymbol = computed(() =>
  String(info.value.balanceSymbol || "USDT"),
);
const priceSymbol = computed(() =>
  String(
    info.value.priceSymbol || currentCard.value.balance?.currency || "USD",
  ),
);
const price = computed(() => Number(info.value.price || 1));
const feeRate = computed(() =>
  normalizeFeeRate(
    isTransferIn.value ? info.value.chargeFeeRate : info.value.withdrawFeeRate,
  ),
);
const cardBalanceValue = computed(() =>
  Number(currentCard.value.balance?.available || 0),
);
const cardBalanceText = computed(() =>
  `${formatFixed2(cardBalanceValue.value)} ${currentCard.value.balance?.currency || "USD"}`.trim(),
);
const availableBalanceValue = computed(() => Number(info.value.balance || 0));
const availableBalanceText = computed(() =>
  `${formatFixed2(availableBalanceValue.value)} ${balanceSymbol.value}`.trim(),
);
const amountNumber = computed(() =>
  Number(String(amount.value || "").trim() || 0),
);
const minAmountValue = computed(() =>
  Number(
    isTransferIn.value ? info.value.chargeMin : info.value.withdrawMin || 0,
  ),
);
const minAmountText = computed(() => {
  const min = isTransferIn.value
    ? info.value.chargeMin
    : info.value.withdrawMin;
  return `${formatFixed2(Number(min || 0))} ${isTransferIn.value ? balanceSymbol.value : currentCard.value.balance?.currency || "USD"}`;
});
const inputSymbol = computed(() =>
  isTransferIn.value
    ? balanceSymbol.value
    : currentCard.value.balance?.currency || "USD",
);
const amountTitle = computed(() =>
  isTransferIn.value
    ? t("card.rechargeAmountTitle")
    : t("wallet.withdrawAmount"),
);
const amountPlaceholder = computed(() =>
  isTransferIn.value
    ? t("card.enterChargeAmount")
    : t("wallet.enterWithdrawAmount"),
);
const feeLabel = computed(() =>
  isTransferIn.value ? t("card.chargeFeeLabel") : t("card.withdrawFeeLabel"),
);
const arrivalLabel = computed(() =>
  isTransferIn.value
    ? t("card.chargeArrivalTitle")
    : t("card.expectedAccountArrival"),
);
const feeValue = computed(() => {
  if (!amountNumber.value) return 0;
  if (isTransferIn.value) {
    return ceilToTwo(amountNumber.value * feeRate.value);
  }
  return ceilToTwo(amountNumber.value * feeRate.value * price.value);
});
const arrivalValue = computed(() => {
  if (!amountNumber.value) return 0;
  if (isTransferIn.value) {
    return truncateToTwo(
      Math.max(amountNumber.value - feeValue.value, 0) * price.value,
    );
  }
  return truncateToTwo(Math.max(amountNumber.value - feeValue.value, 0));
});
const feeText = computed(
  () =>
    `${formatFixed2(feeValue.value)} ${isTransferIn.value ? balanceSymbol.value : priceSymbol.value}`,
);
const arrivalText = computed(
  () => `${formatFixed2(arrivalValue.value)} ${priceSymbol.value}`,
);
const priceText = computed(
  () => `1 ${balanceSymbol.value} ≈ ${price.value} ${priceSymbol.value}`,
);
const cardTitleText = computed(
  () =>
    `${t("card.cardTitlePrefix")}${currentCard.value.network || "WinPay"} (${currentCard.value.bin || "****"})`,
);
const maskedCardNo = computed(() => formatCardCode(currentCard.value));
const successDescription = computed(() =>
  isTransferIn.value
    ? t("card.transferInSuccessDesc")
    : t("card.transferOutSuccessDesc"),
);

function formatFixed2(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) {
    return "0.00";
  }
  return num.toFixed(2);
}

function ceilToTwo(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || num <= 0) {
    return 0;
  }
  return Math.ceil((num - 0.00000001) * 100) / 100;
}

function truncateToTwo(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || num <= 0) {
    return 0;
  }
  return Math.floor((num + 0.00000001) * 100) / 100;
}

function formatCardCode(card) {
  const bin = String(card?.bin || "****").slice(0, 4);
  const last4 = String(card?.last4 || "****");
  return `${bin}-****-****-${last4}`;
}

function formatCardBalance(card) {
  return `${formatFixed2(Number(card?.balance?.available || 0))} ${card?.balance?.currency || "USD"}`.trim();
}

function fillAll() {
  amount.value = String(
    isTransferIn.value
      ? info.value.balance || "0"
      : currentCard.value.balance?.available || "0",
  );
}

function openCardPicker() {
  if (cards.value.length <= 1) {
    return;
  }
  showCardPicker.value = true;
}

async function selectCard(item) {
  currentCardId.value = String(item?.id || "");
  amount.value = "";
  showCardPicker.value = false;
  await navigateWithState(router, route.path, {
    namespace: "card-transfer",
    replace: true,
    payload: {
      ...pageState.value,
      cardId: item?.id || "",
      network: item?.network || "",
      bin: item?.bin || "",
      last4: item?.last4 || "",
      channelType: item?.channelType || "",
      balanceAvailable: item?.balance?.available || "",
      balanceCurrency: item?.balance?.currency || "",
      status: item?.status || "",
    },
  });
  await loadTransferInfo();
}

function resetPayPasswordPopup() {
  payPassword.value = "";
  showPayPassword.value = false;
}

function goForgotPassword() {
  resetPayPasswordPopup();
  router.push("/auth/forgot-pay-password");
}

async function confirmPassword() {
  const payPwd = String(payPassword.value || "").trim();
  if (!payPwd) {
    showFailToast(t("auth.enterPassword"));
    return;
  }

  const result = await payPwdCheck({ payPwd });
  if (!result) {
    showFailToast(t("auth.passwordError"));
    return;
  }

  resetPayPasswordPopup();
  await performTransfer();
}

async function loadCards() {
  await cardStore.fetchCards({
    address: profileStore.platformAddress || "",
    cardType: cardType.value,
    deviceToken: getDeviceId(),
  });

  if (!currentCardId.value && cards.value.length) {
    currentCardId.value = String(cards.value[0].id || "");
  }
}

async function loadTransferInfo() {
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  if (!currentCard.value?.id) {
    info.value = {};
    return;
  }

  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });
  try {
    const result = isTransferIn.value
      ? await pcCardChargeInfo({
          address: profileStore.platformAddress,
          cardId: currentCard.value.id,
        })
      : await pcCardWithdrawInfo({
          address: profileStore.platformAddress,
          cardId: currentCard.value.id,
        });
    info.value = result || {};
  } catch (error) {
    showFailToast(error.message || t("common.retryLater"));
  } finally {
    closeToast();
  }
}

function submitTransfer() {
  if (!currentCard.value?.id) {
    showFailToast(t("card.empty"));
    return;
  }

  if (!String(amount.value || "").trim()) {
    showFailToast(
      isTransferIn.value
        ? t("card.enterChargeAmount")
        : t("wallet.enterWithdrawAmount"),
    );
    return;
  }

  if (
    !amountNumber.value ||
    Number.isNaN(amountNumber.value) ||
    amountNumber.value <= 0
  ) {
    showFailToast(t("card.enterValidAmount"));
    return;
  }

  if (minAmountValue.value > 0 && amountNumber.value < minAmountValue.value) {
    showFailToast(`${t("card.minAmount")} ${minAmountText.value}`);
    return;
  }

  if (isTransferIn.value && amountNumber.value > availableBalanceValue.value) {
    showFailToast(
      `${t("wallet.availableBalance")} ${availableBalanceText.value}`,
    );
    return;
  }

  if (!isTransferIn.value && amountNumber.value > cardBalanceValue.value) {
    showFailToast(`${t("card.cardBalance")} ${cardBalanceText.value}`);
    return;
  }

  showPayPassword.value = true;
}

async function performTransfer() {
  if (!profileStore.platformAddress || !currentCard.value?.id) {
    return;
  }

  submitting.value = true;
  showLoadingToast({
    message: t("common.submitting"),
    forbidClick: true,
    duration: 0,
  });
  try {
    const payload = {
      address: profileStore.platformAddress,
      amount: String(amount.value || "").trim(),
      cardId: currentCard.value.id,
    };

    if (isTransferIn.value) {
      await pcCardCharge(payload);
    } else {
      await pcCardWithdraw(payload);
    }

    await Promise.allSettled([
      loadCards(),
      loadTransferInfo(),
      cardStore.fetchRecentRecords({
        address: profileStore.platformAddress,
        cardType: cardType.value,
        cardId: currentCard.value.id,
        pageNum: 1,
        pageSize: 3,
        lastDays: "",
      }),
    ]);

    amount.value = "";
    showSuccessPopup.value = true;
  } catch (error) {
    showFailToast(error.message || t("common.retryLater"));
  } finally {
    submitting.value = false;
    closeToast();
  }
}

function handleSuccessDone() {
  showSuccessPopup.value = false;
  router.back();
}

onMounted(async () => {
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }
  await loadCards();
  await loadTransferInfo();
});
</script>

<style scoped lang="scss">
.card-transfer-page {
  min-height: var(--app-viewport-height);
  padding: 0 15px calc(24px + env(safe-area-inset-bottom));
  background: #f5f5f5;
}

.card-preview {
  position: relative;
  overflow: hidden;
  margin-top: 15px;
  border-radius: 8px;
  color: #ffffff;
}

.card-preview__bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.card-preview__content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 16px;
}

.card-preview__block {
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    font-size: 14px;
    color: #ffffff;
  }

  [data-strong] {
    font-size: 15px;
    font-weight: 500;
    color: #ffffff;
  }
}

.card-preview__block--right {
  align-items: flex-end;
  text-align: right;
}

.panel-card {
  margin-top: 15px;
  padding: 16px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(17, 17, 17, 0.04);
}

.section-title h2 {
  margin: 0;
  color: #000000;
  font-size: 16px;
  font-weight: 500;
}

.section-title__row {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  flex-wrap: wrap;

  span {
    color: rgba(0, 0, 0, 0.45);
    font-size: 14px;
    line-height: 20px;
  }
}

.section-title--amount {
  margin-top: 2px;
}

.asset-row {
  display: flex;
  align-items: center;
  margin-top: 15px;
  padding: 16px 12px;
  border-radius: 10px;
  background: #f4f5f6;

  > .app-image {
    width: 36px;
    height: 36px;
    margin-right: 10px;
  }
}

.asset-row__content {
  display: flex;
  flex-direction: column;
  gap: 4px;

  [data-strong] {
    color: #1d2129;
    font-size: 16px;
    font-weight: 500;
  }

  span {
    color: #999999;
    font-size: 12px;
  }
}

.hint-line,
.result-row {
  display: flex;
  align-items: center;
  gap: 12px;

  span {
    color: #86909c;
    font-size: 13px;
  }
}

.hint-line {
  margin-top: 10px;
  justify-content: flex-start;
  gap: 5px;

  [data-strong] {
    color: #1d2129;
    font-size: 13px;
    font-weight: 500;
    text-align: left;
  }
}

.hint-line--compact {
  margin-top: 8px;
}

.result-row {
  justify-content: space-between;
  font-size: 15px;
  [data-strong] {
    color: #1d2129;
    font-weight: 700;
    text-align: right;
  }
}

.amount-field {
  margin-top: 16px;
  border-radius: 10px;
  background: #f4f5f6;

  :deep(.van-cell) {
    padding: 14px 12px;
    background: transparent;
  }

  :deep(.van-field__control) {
    font-size: 16px;
  }

  :deep(.van-field__control::placeholder) {
    color: #666;
  }
}

.amount-field__actions {
  display: flex;
  align-items: center;
  gap: 7px;
}

.amount-symbol {
  color: #1d2129;
  font-size: 16px;
  font-weight: bold;
}

.amount-all-btn {
  :deep(.van-button) {
    height: 30px;
    padding: 0 10px;
  }
}

.dash-line {
  width: 100%;
  height: 1px;
  margin: 16px 0;
  border-top: 1px dashed #d8dde7;
}

.dash-line--spacious {
  margin: 16px 0 18px;
}

.submit-btn {
  margin-top: 30px;

  :deep(.van-button) {
    height: 44px;
  }
}

.card-picker-popup {
  background: transparent;
}

.card-picker {
  padding: 18px 14px calc(24px + env(safe-area-inset-bottom));
  background: #ffffff;
}

.card-picker__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  [data-strong] {
    color: #000000;
    font-size: 16px;
    font-weight: 500;
  }

  button {
    padding: 0;
    border: 0;
    background: transparent;
    color: #86909c;
    font-size: 18px;
  }
}

.card-picker__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.card-picker__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 12px;
  border: 0;
  border-radius: 8px;
  background: #f7f8fa;
  color: #1d2129;
  text-align: left;

  > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  [data-strong] {
    font-size: 14px;
    font-weight: 500;
  }

  span {
    color: #86909c;
    font-size: 12px;
  }
}

.card-picker__item.active {
  background: #edf4ff;
}

.success-popup {
  width: calc(100% - 48px);
  max-width: 320px;
  overflow: visible;
  background: transparent;
}

.success-dialog {
  position: relative;
  padding: 18px 20px 20px;
  border-radius: 16px;
  background: #ffffff;
  text-align: center;

  [data-strong] {
    display: block;
    margin-top: 8px;
    color: rgba(0, 0, 0, 0.85);
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  p {
    margin: 8px 0 28px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 14px;
    line-height: 22px;
  }

  :deep(.van-button) {
    height: 44px;
    font-size: 16px;
    font-weight: 500;
    border-radius: 10px;
  }
}

.success-dialog__close {
  position: absolute;
  top: 14px;
  right: 14px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #999999;
  font-size: 18px;
}

.success-dialog__icon {
  display: block;
  width: 72px;
  height: 72px;
  margin: 34px auto 0;
  object-fit: contain;
}
</style>
